/**
 * Navigation direction: did the last navigation push, pop or replace?
 *
 * Vue Router writes a `position` into `history.state` for every entry, so comparing it before and after a
 * navigation tells a push (deeper), a pop (browser / Android back) and a replace (tab switches on native,
 * query tweaks) apart. Transitions read `navDirection` to pick a slide or a cross-fade instead of guessing
 * from route names.
 *
 * Also the tab-reselect signal: tapping the tab you're already on asks the page to pop its detail, else
 * scroll to the top (UITabBarController behaviour). Detail panes (NativeStackPane) register a handler while
 * they're open; Dashboard falls back to scrolling the tab's scroller.
 */
import { readonly, ref } from 'vue'
import type { Router } from 'vue-router'

export type NavDirection = 'push' | 'pop' | 'replace'

/** Pure: the direction between two history positions (undefined = unknown, e.g. the first load). */
export function directionFrom(prev: number | null | undefined, next: number | null | undefined): NavDirection {
    if (typeof prev !== 'number' || typeof next !== 'number') return 'replace'
    if (next > prev) return 'push'
    if (next < prev) return 'pop'
    return 'replace'
}

const direction = ref<NavDirection>('replace')
/** The direction of the most recent finished navigation. */
export const navDirection = readonly(direction)

function historyPosition(): number | undefined {
    if (typeof window === 'undefined') return undefined
    const pos = (window.history.state as { position?: unknown } | null)?.position
    return typeof pos === 'number' ? pos : undefined
}

const installed = new WeakSet<Router>()
/** Track `navDirection` for this router. Idempotent; called where the router is created. */
export function installNavDirection(router: Router): void {
    if (installed.has(router)) return
    installed.add(router)
    let last = historyPosition()
    router.afterEach((_to, _from, failure) => {
        if (failure) return
        const pos = historyPosition()
        direction.value = directionFrom(last, pos)
        last = pos
    })
}

// ── Tab reselect ─────────────────────────────────────────────────────────────────────────────────────────
/** Return true when handled (a detail popped); false passes it on (in the end: scroll to top). */
export type TabReselectHandler = (tab: string) => boolean | void

const reselectHandlers: { fn: TabReselectHandler; seq: number }[] = []
let reselectSeq = 0

/** Register a reselect handler (newest first). Returns the unregister function. */
export function onTabReselect(fn: TabReselectHandler): () => void {
    const entry = { fn, seq: ++reselectSeq }
    reselectHandlers.push(entry)
    return () => {
        const at = reselectHandlers.indexOf(entry)
        if (at >= 0) reselectHandlers.splice(at, 1)
    }
}

/** The active tab was tapped again: run handlers newest-first until one handles it. */
export function runTabReselect(tab: string): boolean {
    const ordered = [...reselectHandlers].sort((a, b) => b.seq - a.seq)
    for (const { fn } of ordered) {
        if (fn(tab) === true) return true
    }
    return false
}

/**
 * Scroll the first scrolled-down scroller inside `root` back to the top (smoothly unless reduced motion).
 * Returns whether anything scrolled. Breadth-first so the page's main scroller wins over nested ones.
 */
export function scrollFirstScrollerToTop(root: Element | null | undefined, smooth = true): boolean {
    if (!root) return false
    const queue: Element[] = [root]
    while (queue.length) {
        const el = queue.shift()!
        if (el instanceof HTMLElement && el.scrollTop > 0 && el.scrollHeight > el.clientHeight) {
            const cs = getComputedStyle(el)
            if (/(auto|scroll)/.test(cs.overflowY) && !el.closest('[inert]')) {
                el.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' })
                return true
            }
        }
        queue.push(...Array.from(el.children))
    }
    return false
}

/** The tab host Dashboard renders the active tab in. */
export const TAB_HOST_SELECTOR = '[data-dash-tab]'

/**
 * The tab bar's reselect: pop the open detail if a page claims it, else scroll the tab to the top.
 * Returns what happened (for tests and logging).
 */
export function reselectTab(tab: string): 'popped' | 'scrolled' | 'none' {
    if (runTabReselect(tab)) return 'popped'
    if (typeof document === 'undefined') return 'none'
    return scrollFirstScrollerToTop(document.querySelector(TAB_HOST_SELECTOR), !prefersReducedMotionQuery()) ? 'scrolled' : 'none'
}

function prefersReducedMotionQuery(): boolean {
    return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
}

/** Test-only. */
export function __resetNavDirectionForTests(): void {
    direction.value = 'replace'
    reselectHandlers.length = 0
}
